<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { ref, inject } from "vue"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { watchDebounced } from "@vueuse/core"
import { useToast } from "primevue/usetoast"
import { useAuthStore } from "@core/stores/auth.store"
import type { Services } from "@services"
import type { UsageProfile } from "@modules/usage-profile/types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Profils d'utilisation",
            confirm_delete_title: "Supprimer le profil {name} ?",
            confirm_duplicate_title: "Dupliquer le profil {name} ?",
            banner: "Gérez les profils d'utilisation de votre entreprise.",
            search_placeholder: "Rechercher un profil…",
            confirm_delete_message: "Cette action est irréversible.",
            confirm_delete_button: "Supprimer",
            new_name_label: "Nom du nouveau profil",
            new_name_placeholder: "Copie de…",
            new_name_error: "Le nom est requis",
            create: "Créer un nouveau profil",
            create_dialog_title: "Créer un nouveau profil",
            edit_dialog_suffix: "Édition",
            success_delete: "Profil supprimé avec succès.",
            success_duplicate: "Profil dupliqué avec succès.",
            cancel: "Annuler",
            duplicate_button: "Dupliquer",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
// Aucune prop : companyId et isCompanyAdmin viennent du store auth

// ================================
// STORES
// ================================
const authStore = useAuthStore()
const { isCompanyAdmin, companyId } = storeToRefs(authStore)

// ================================
// SERVICES
// ================================
const services = inject<Services>("services")!
const toast = useToast()

// ================================
// STATE
// ================================
/** Terme de recherche */
const search = ref("")
/** Liste des profils chargée */
const profiles = ref<UsageProfile[]>([])
/** Message d’erreur global */
const error = ref<string | null>(null)
/** Chargement en cours (fetch) */
const pending = ref(false)
/** Profil sélectionné pour les dialogs (suppression, duplication, édition) */
const currentProfile = ref<UsageProfile | null>(null)
/** Chargement en cours lors de la suppression */
const loadingDelete = ref(false)
/** Message d’erreur après suppression */
const errorDelete = ref("")
/** Chargement en cours lors de la duplication */
const loadingDuplicate = ref(false)
/** Message d’erreur après duplication */
const errorDuplicate = ref("")
/** Nom du profil dupliqué */
const newName = ref("")
/** Visibilité du dialog de suppression */
const isDeleteDialogVisible = ref(false)
/** Visibilité du dialog de duplication */
const isDuplicateDialogVisible = ref(false)
/** Visibilité du dialog d’édition/création */
const isEditDialogVisible = ref(false)

// ================================
// BUSINESS LOGIC (fetch)
// ================================
/** Charge la liste des profils (avec recherche) */
async function fetchProfiles(): Promise<void> {
    try {
        pending.value = true
        error.value = null
        profiles.value = await services.$getUsageProfiles({
            noPage: true,
            search: search.value || undefined,
        })
    } catch (err) {
        error.value = (err as Error).message
    } finally {
        pending.value = false
    }
}

// ================================
// INTERACTIONS
// ================================
/** Met à jour le profil courant (pour dialogs) */
function updateCurrentProfile(profileId: string | null = null): void {
    currentProfile.value = profileId
        ? (profiles.value.find((p) => p._id === profileId) ?? null)
        : null
}

/** Ouvre le dialog de création de profil */
function onClickCreateProfile(): void {
    updateCurrentProfile(null)
    isEditDialogVisible.value = true
}

/** Ouvre le dialog d’édition pour le profil donné */
function onClickEdit(profileId: string, _toTarget?: string): void {
    updateCurrentProfile(profileId)
    isEditDialogVisible.value = true
}

/** Handlers pour les actions d’édition ciblées (évite any dans le template) */
function onEditWeek(profileId: string): void {
    onClickEdit(profileId, "edit-week")
}
function onEditException(profileId: string): void {
    onClickEdit(profileId, "edit-exception")
}
function onEditBuildings(profileId: string): void {
    onClickEdit(profileId, "edit-buildings")
}

/** Callback après création d’un profil */
function onCreateProfile(profile: UsageProfile): void {
    profiles.value.push(profile)
    updateCurrentProfile(null)
    isEditDialogVisible.value = false
}

/** Callback après modification d’un profil */
function onModifyProfile(profile: UsageProfile): void {
    const idx = profiles.value.findIndex(
        (p) => p._id === currentProfile.value?._id
    )
    if (idx >= 0) profiles.value.splice(idx, 1, profile)
    updateCurrentProfile(null)
    isEditDialogVisible.value = false
}

/** Ouvre le dialog de confirmation de suppression pour le profil donné */
function onClickRemove(profileId: string): void {
    updateCurrentProfile(profileId)
    errorDelete.value = ""
    isDeleteDialogVisible.value = true
}

/** Supprime le profil courant et met à jour la liste */
async function onConfirmDelete(): Promise<void> {
    if (!currentProfile.value) return
    try {
        errorDelete.value = ""
        loadingDelete.value = true
        await services.$deleteUsageProfile({
            profileId: currentProfile.value._id,
        })
        profiles.value = profiles.value.filter(
            (p) => p._id !== currentProfile.value?._id
        )
        isDeleteDialogVisible.value = false
        toast.add({
            severity: "success",
            summary: t("success_delete"),
        })
    } catch (err) {
        errorDelete.value = (err as Error).message
    } finally {
        loadingDelete.value = false
    }
}

/** Ouvre le dialog de duplication pour le profil donné */
function onClickDuplicate(profileId: string): void {
    updateCurrentProfile(profileId)
    newName.value = `Copie de ${currentProfile.value?.name ?? ""}`
    errorDuplicate.value = ""
    isDuplicateDialogVisible.value = true
}

/** Duplique le profil courant avec le nouveau nom */
async function onConfirmDuplicate(): Promise<void> {
    if (!currentProfile.value || !newName.value.trim()) return
    try {
        errorDuplicate.value = ""
        loadingDuplicate.value = true
        const newProfile = await services.$createUsageProfile({
            profile: {
                name: newName.value.trim(),
                profil_semaine: currentProfile.value.profil_semaine,
                fermeture_jourferie: currentProfile.value.fermeture_jourferie,
                fermeture_vacances: currentProfile.value.fermeture_vacances,
            },
        })
        profiles.value.unshift(newProfile)
        isDuplicateDialogVisible.value = false
        toast.add({
            severity: "success",
            summary: t("success_duplicate"),
        })
    } catch (err) {
        errorDuplicate.value = (err as Error).message
    } finally {
        loadingDuplicate.value = false
    }
}

/** Ferme le dialog de suppression et réinitialise le profil courant */
function onCloseDeleteDialog(): void {
    isDeleteDialogVisible.value = false
    updateCurrentProfile(null)
}

/** Ferme le dialog de duplication et réinitialise le profil courant */
function onCloseDuplicateDialog(): void {
    isDuplicateDialogVisible.value = false
    updateCurrentProfile(null)
}

/** Ferme le dialog d’édition et réinitialise le profil courant */
function onCloseEditDialog(): void {
    isEditDialogVisible.value = false
    updateCurrentProfile(null)
}

// ================================
// WATCHERS
// ================================
watchDebounced(search, fetchProfiles, { debounce: 500, maxWait: 1000 })

// Chargement initial
fetchProfiles()
</script>

<template>
    <BaseTitledCard
        class="usage-profile-list-card"
        content-class="flex flex-col gap-4"
    >
        <template #header>
            {{ t("title") }}
        </template>
        <template #content>
            <BaseInputText
                v-model="search"
                class="w-full max-w-md mb-4"
                :disabled="pending"
                :placeholder="t('search_placeholder')"
            />
            <div v-if="pending" class="flex flex-col gap-2 min-h-80">
                <BaseSkeleton
                    v-for="i in 7"
                    :key="i"
                    height="2.8rem"
                    width="100%"
                />
            </div>
            <UsageProfileList
                v-else
                class="usage-profile-list-card__list"
                :profiles="profiles"
                :search="search"
                :editable="isCompanyAdmin"
                :disabled-empty-message="isCompanyAdmin"
                @click:duplicate="onClickDuplicate"
                @click:remove="onClickRemove"
                @click:edit="onClickEdit"
                @edit:week="onEditWeek"
                @edit:exception="onEditException"
                @edit:buildings="onEditBuildings"
            />
            <BaseButton
                v-if="isCompanyAdmin"
                class="mx-auto"
                text
                @click="onClickCreateProfile"
            >
                {{ t("create") }}
            </BaseButton>
            <BaseAppMessage v-if="error" severity="error" :message="error" />

            <!-- Dialog suppression -->
            <BaseDialog
                v-model:visible="isDeleteDialogVisible"
                modal
                position="bottom"
                :header="
                    currentProfile
                        ? t('confirm_delete_title', {
                              name: currentProfile.name,
                          })
                        : ''
                "
                :closable="true"
                :close-on-escape="true"
                @hide="onCloseDeleteDialog"
            >
                <p class="text-center">
                    {{ t("confirm_delete_message") }}
                </p>
                <template #footer>
                    <BaseButton
                        :label="t('cancel')"
                        variant="text"
                        @click="onCloseDeleteDialog"
                    />
                    <BaseButton
                        :label="t('confirm_delete_button')"
                        severity="danger"
                        :loading="loadingDelete"
                        @click="onConfirmDelete"
                    />
                </template>
                <BaseAppMessage
                    v-if="errorDelete"
                    severity="error"
                    :message="errorDelete"
                    class="mt-4"
                />
            </BaseDialog>

            <!-- Dialog duplication -->
            <BaseDialog
                v-model:visible="isDuplicateDialogVisible"
                modal
                :header="
                    currentProfile
                        ? t('confirm_duplicate_title', {
                              name: currentProfile.name,
                          })
                        : ''
                "
                :closable="true"
                :close-on-escape="true"
                position="bottom"
                @hide="onCloseDuplicateDialog"
            >
                <BaseInputWrapper
                    input-id="duplicate-name"
                    :label="t('new_name_label')"
                >
                    <BaseInputText
                        v-model="newName"
                        class="w-full"
                        :disabled="loadingDuplicate"
                        :placeholder="t('new_name_placeholder')"
                    />
                </BaseInputWrapper>
                <BaseAppMessage
                    v-if="errorDuplicate"
                    severity="error"
                    :message="errorDuplicate"
                    class="mt-4"
                />
                <template #footer>
                    <BaseButton
                        :label="t('cancel')"
                        variant="text"
                        @click="onCloseDuplicateDialog"
                    />
                    <BaseButton
                        :label="t('duplicate_button')"
                        :disabled="!newName?.trim()"
                        :loading="loadingDuplicate"
                        @click="onConfirmDuplicate"
                    />
                </template>
            </BaseDialog>

            <!-- Dialog édition -->
            <BaseDialog
                v-model:visible="isEditDialogVisible"
                position="bottom"
                modal
                :header="
                    currentProfile
                        ? t('title') +
                          ' - ' +
                          (currentProfile.name || t('edit_dialog_suffix'))
                        : t('create_dialog_title')
                "
                :style="{ width: '50vw' }"
                :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
                :closable="true"
                :close-on-escape="true"
                @hide="onCloseEditDialog"
            >
                <UsageProfileEdit
                    v-if="companyId"
                    :company-id="companyId"
                    :building-id="null"
                    :profile="currentProfile ?? undefined"
                    @click:create="onCreateProfile"
                    @click:modify="onModifyProfile"
                />
            </BaseDialog>
        </template>
    </BaseTitledCard>
</template>
